import { database } from '../config/backend';
import { ref, set as rtdbSet, push, update as rtdbUpdate, get as rtdbGet, onValue, off } from './phpDataAdapter';
import { createMuxLiveStream, endMuxLiveStream, getMuxPlaybackUrl } from '../config/mux';

export interface MuxLiveStream {
    id: string;
    muxStreamId?: string;
    muxStreamKey?: string;
    muxPlaybackId?: string;
    hostId: string;
    title: string;
    description?: string;
    status: 'idle' | 'starting' | 'live' | 'ended';
    rtmpUrl?: string;
    playbackUrl?: string;
    viewerCount: number;
    createdAt: Date;
    startedAt?: Date;
    endedAt?: Date;
}

class MuxLiveService {
    /**
     * Cria uma nova live stream
     */
    async createLiveStream(
        hostId: string,
        title: string,
        description?: string
    ): Promise<MuxLiveStream> {
        try {
            const streamRef = ref(database, 'liveStreams');
            const newStreamRef = push(streamRef);
            const streamId = newStreamRef.key || '';

            // Criar via API PHP da Hostinger
            const streamData = await createMuxLiveStream(streamId, hostId, title, description);

            const liveStream: MuxLiveStream = {
                id: streamId,
                muxStreamId: streamData.streamId,
                muxStreamKey: streamData.streamKey,
                muxPlaybackId: streamData.playbackId,
                hostId,
                title,
                description,
                status: 'idle',
                rtmpUrl: streamData.rtmpUrl,
                playbackUrl: getMuxPlaybackUrl(streamData.playbackId),
                viewerCount: 0,
                createdAt: new Date(),
            };

            await rtdbSet(newStreamRef, {
                ...liveStream,
                createdAt: liveStream.createdAt.toISOString(),
            });

            return liveStream;
        } catch (error) {
            console.error('Erro ao criar live stream:', error);
            throw error;
        }
    }

    /**
     * Inicia transmissão (muda status para live)
     */
    async startBroadcast(streamId: string): Promise<void> {
        try {
            const streamRef = ref(database, `liveStreams/${streamId}`);
            await rtdbUpdate(streamRef, {
                status: 'live',
                startedAt: new Date().toISOString(),
            });
        } catch (error) {
            console.error('Erro ao iniciar broadcast:', error);
            throw error;
        }
    }

    /**
     * Finaliza transmissão
     */
    async endBroadcast(streamId: string): Promise<void> {
        try {
            // Finalizar no Mux
            await endMuxLiveStream(streamId);

            // Atualizar PHP/MySQL
            const streamRef = ref(database, `liveStreams/${streamId}`);
            const streamSnap = await rtdbGet(streamRef);

            if (streamSnap.exists()) {
                const stream = streamSnap.val();
                const startedAt = stream.startedAt ? new Date(stream.startedAt) : new Date();
                const endedAt = new Date();
                const duration = Math.floor((endedAt.getTime() - startedAt.getTime()) / 1000);

                await rtdbUpdate(streamRef, {
                    status: 'ended',
                    endedAt: endedAt.toISOString(),
                    duration,
                });
            }
        } catch (error) {
            console.error('Erro ao finalizar broadcast:', error);
            throw error;
        }
    }

    /**
     * Busca uma live stream
     */
    async getLiveStream(streamId: string): Promise<MuxLiveStream | null> {
        try {
            const streamRef = ref(database, `liveStreams/${streamId}`);
            const streamSnap = await rtdbGet(streamRef);

            if (!streamSnap.exists()) {
                return null;
            }

            const data = streamSnap.val();
            return {
                ...data,
                createdAt: new Date(data.createdAt),
                startedAt: data.startedAt ? new Date(data.startedAt) : undefined,
                endedAt: data.endedAt ? new Date(data.endedAt) : undefined,
            } as MuxLiveStream;
        } catch (error) {
            console.error('Erro ao buscar live stream:', error);
            return null;
        }
    }

    /**
     * Busca lives ativas
     */
    async getActiveLiveStreams(hostId?: string): Promise<MuxLiveStream[]> {
        try {
            const streamsRef = ref(database, 'liveStreams');
            const streamsSnap = await rtdbGet(streamsRef);

            if (!streamsSnap.exists()) {
                return [];
            }

            const streams = streamsSnap.val() as Record<string, any>;
            const result: MuxLiveStream[] = [];

            for (const [id, data] of Object.entries(streams)) {
                if (data.status === 'live' && (!hostId || data.hostId === hostId)) {
                    result.push({
                        id,
                        ...data,
                        createdAt: new Date(data.createdAt),
                        startedAt: data.startedAt ? new Date(data.startedAt) : undefined,
                        endedAt: data.endedAt ? new Date(data.endedAt) : undefined,
                    } as MuxLiveStream);
                }
            }

            // Ordenar por data (mais recente primeiro)
            result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

            return result;
        } catch (error) {
            console.error('Erro ao buscar lives ativas:', error);
            return [];
        }
    }

    /**
     * Observa mudanças em uma live stream
     */
    watchLiveStream(
        streamId: string,
        callback: (stream: MuxLiveStream | null) => void
    ): () => void {
        const streamRef = ref(database, `liveStreams/${streamId}`);

        const unsubscribe = onValue(streamRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                callback({
                    ...data,
                    createdAt: new Date(data.createdAt),
                    startedAt: data.startedAt ? new Date(data.startedAt) : undefined,
                    endedAt: data.endedAt ? new Date(data.endedAt) : undefined,
                } as MuxLiveStream);
            } else {
                callback(null);
            }
        });

        return () => off(streamRef, 'value', unsubscribe);
    }

    /**
     * Incrementa contador de viewers
     */
    async incrementViewerCount(streamId: string): Promise<void> {
        try {
            const streamRef = ref(database, `liveStreams/${streamId}`);
            const streamSnap = await rtdbGet(streamRef);

            if (streamSnap.exists()) {
                const currentCount = streamSnap.val().viewerCount || 0;
                await rtdbUpdate(streamRef, {
                    viewerCount: currentCount + 1,
                });
            }
        } catch (error) {
            console.error('Erro ao incrementar viewer count:', error);
        }
    }

    /**
     * Decrementa contador de viewers
     */
    async decrementViewerCount(streamId: string): Promise<void> {
        try {
            const streamRef = ref(database, `liveStreams/${streamId}`);
            const streamSnap = await rtdbGet(streamRef);

            if (streamSnap.exists()) {
                const currentCount = streamSnap.val().viewerCount || 0;
                await rtdbUpdate(streamRef, {
                    viewerCount: Math.max(0, currentCount - 1),
                });
            }
        } catch (error) {
            console.error('Erro ao decrementar viewer count:', error);
        }
    }
}

// Singleton
export const muxLiveService = new MuxLiveService();
export default muxLiveService;
